import type { Lab, LinearRGB } from '../../core/color';

interface StreetWalls {
  /**
   * The painting's walls along the street at street scale: the median of the painting's pixels within 4 px of the
   * street's houses, classed by the nearest palette class and keeping the wall-like classes (paint_region.py).
   */
  readonly painted: Lab;
  /** The street's own house walls as rendered at walking height with the palette untinted, over six views. */
  readonly walking: Lab;
}

/**
 * The streets whose houses cover 2,000 or more wall-like painting pixels and whose painted median is not greener
 * than the walls' hue (docs/plans/houses.md, section 8.11). A street's own ratio varies with the share of its walls
 * the sun lights in the views (correlation -0.85), so it is the lighting of those views, not the street's albedo:
 * every wall takes the median ratio.
 */
export const WALL_MEASURES: Readonly<Record<string, StreetWalls>> = {
  'quay-nieuwezijds-achterburgwal': { painted: [44.2, 3.83, 33.56], walking: [54.47, 3.3, 36.96] },
  'street-nieuwendijk': { painted: [45.67, 5.15, 34.68], walking: [52.49, 1.18, 36.71] },
  'lane-singel': { painted: [43.1, 2.65, 33.67], walking: [44.79, 2.93, 38.12] },
  'quay-nieuwezijds-voorburgwal': { painted: [44.92, 4.24, 34.46], walking: [46.54, 1.44, 38.49] },
  'quay-oudezijds-voorburgwal': { painted: [43.81, 3.82, 33.83], walking: [54.89, 2.41, 37.06] },
  'street-kalverstraat': { painted: [42.99, 4.39, 34.35], walking: [50.46, 3.22, 37.91] },
  'quay-oudezijds-achterburgwal': { painted: [42.02, 3.14, 32.32], walking: [42.42, 3.54, 41.52] },
  'street-warmoesstraat': { painted: [43.86, 3.55, 35.45], walking: [48.81, 3.46, 38.51] },
  'quay-damrak-west': { painted: [45.19, 3.58, 33.92], walking: [55.07, 3.58, 37.53] },
  'street-zeedijk': { painted: [43.4, 2.6, 32.24], walking: [47.14, 3.27, 38.59] },
  'street-nes': { painted: [45.01, 2.31, 34.05], walking: [43.35, 3.73, 40.6] },
  'quay-rokin-north-west': { painted: [43.34, 4.38, 32.88], walking: [53.92, 3.65, 38.39] },
  'street-nieuwebrugsteeg': { painted: [44.39, 4.51, 35.2], walking: [47.68, 3.4, 37.14] },
};

const WHITE = [0.95047, 1, 1.08883] as const;
const XYZ_TO_LINEAR = [
  [3.2404542, -1.5371385, -0.4985314],
  [-0.969266, 1.8760108, 0.041556],
  [0.0556434, -0.2040259, 1.0572252],
] as const;

/** The inverse of `linearToLab` (core/color.ts): D65, the same breakpoint. */
export function labToLinear([l, a, b]: Lab): LinearRGB {
  const fy = (l + 16) / 116;
  const f = [fy + a / 500, fy, fy - b / 200];
  const inv = (t: number) => (t > 0.206893 ? t ** 3 : (t - 16 / 116) / 7.787);
  const xyz = f.map((t, i) => inv(t) * WHITE[i]!);
  const row = (m: readonly number[]) => m[0]! * xyz[0]! + m[1]! * xyz[1]! + m[2]! * xyz[2]!;
  return [row(XYZ_TO_LINEAR[0]), row(XYZ_TO_LINEAR[1]), row(XYZ_TO_LINEAR[2])];
}

function gainOf({ painted, walking }: StreetWalls): LinearRGB {
  const [p, w] = [labToLinear(painted), labToLinear(walking)];
  return [p[0] / w[0], p[1] / w[1], p[2] / w[2]];
}

function median(values: readonly number[]): number {
  const s = [...values].sort((x, y) => x - y);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

const GAINS = Object.values(WALL_MEASURES).map(gainOf);

const channel = (c: number) => median(GAINS.map((g) => g[c]!));

export const WALL_GAIN: LinearRGB = [channel(0), channel(1), channel(2)];

export function tintWall(colour: LinearRGB): LinearRGB {
  return [colour[0] * WALL_GAIN[0], colour[1] * WALL_GAIN[1], colour[2] * WALL_GAIN[2]];
}
